import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, UserPlus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignupPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "",
    hub_id: ""
  });
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchHubs();
  }, []);

  const fetchHubs = async () => {
    try {
      const response = await axios.get(`${API}/hubs/public`);
      setHubs(response.data);
    } catch (error) {
      console.error("Error fetching hubs:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (!formData.role) {
      toast.error("Please select a role");
      return;
    }

    if (!formData.hub_id) {
      toast.error("Please select a hub");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: formData.role,
        hub_id: formData.hub_id
      });
      
      if (response.data.approved === false) {
        setSuccess(true);
        toast.success("Registration successful! Awaiting admin approval.");
      } else {
        toast.success("Registration successful!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50/30 p-4">
        <div className="mb-6 text-center">
          <div className="inline-block p-3 bg-white rounded-2xl shadow-lg mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_hub-dispatch-mgmt/artifacts/ncecubvs_rr%20logo.jpg" 
              alt="Recompose Logo" 
              className="w-20 h-20 sm:w-24 sm:h-24 object-contain"
            />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-800">Recompose</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Recycling Management System</p>
        </div>

        <Card className="w-full max-w-sm sm:max-w-md shadow-xl border border-gray-200" data-testid="signup-success-card">
          <CardContent className="pt-8 pb-8">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-green-600" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Registration Submitted!</h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-xs">
                Your account has been created and is pending admin approval. You will be able to login once an administrator approves your account.
              </p>
              <div className="pt-4 w-full">
                <Link to="/login">
                  <Button 
                    className="w-full h-10 sm:h-11 bg-gradient-to-r from-[#1B5E20] to-[#6BB6A] hover:opacity-90"
                    data-testid="back-to-login-btn"
                  >
                    Back to Login
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50/30 p-4">
      {/* Centered Logo */}
      <div className="mb-4 text-center">
        <div className="inline-block p-3 bg-white rounded-2xl shadow-lg mb-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_hub-dispatch-mgmt/artifacts/ncecubvs_rr%20logo.jpg" 
            alt="Recompose Logo" 
            className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
          />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-800">Recompose</h1>
        <p className="text-xs sm:text-sm text-gray-600 mt-1">Recycling Management System</p>
      </div>

      {/* Signup Form */}
      <Card className="w-full max-w-sm sm:max-w-md shadow-xl border border-gray-200" data-testid="signup-card">
        <CardHeader className="space-y-1 pb-3">
          <CardTitle className="text-lg sm:text-xl font-bold text-center text-gray-800 flex items-center justify-center gap-2">
            <UserPlus className="w-5 h-5" />
            Create Account
          </CardTitle>
          <CardDescription className="text-center text-xs sm:text-sm">
            Register as Staff or Manager
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs sm:text-sm font-semibold">Full Name</Label>
              <Input
                id="name"
                data-testid="signup-name-input"
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="h-9"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs sm:text-sm font-semibold">Email Address</Label>
              <Input
                id="email"
                data-testid="signup-email-input"
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-9"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="role" className="text-xs sm:text-sm font-semibold">Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="signup-role-select" className="h-9">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="hub" className="text-xs sm:text-sm font-semibold">Assigned Hub</Label>
                <Select
                  value={formData.hub_id}
                  onValueChange={(value) => setFormData({ ...formData, hub_id: value })}
                >
                  <SelectTrigger data-testid="signup-hub-select" className="h-9">
                    <SelectValue placeholder="Select hub" />
                  </SelectTrigger>
                  <SelectContent>
                    {hubs.map((hub) => (
                      <SelectItem key={hub.id} value={hub.id}>
                        {hub.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs sm:text-sm font-semibold">Password</Label>
              <Input
                id="password"
                data-testid="signup-password-input"
                type="password"
                placeholder="Minimum 6 characters"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="h-9"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword" className="text-xs sm:text-sm font-semibold">Confirm Password</Label>
              <Input
                id="confirmPassword"
                data-testid="signup-confirm-password-input"
                type="password"
                placeholder="Re-enter your password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="h-9"
                required
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800">
              <strong>Note:</strong> Your account will require admin approval before you can log in.
            </div>

            <Button
              data-testid="signup-submit-button"
              type="submit"
              className="w-full h-9 sm:h-10 bg-gradient-to-r from-[#1B5E20] to-[#6BB6A] hover:opacity-90 transition-all text-sm font-semibold"
              disabled={loading}
            >
              {loading ? "Creating Account..." : "Create Account"}
            </Button>

            <div className="text-center text-xs sm:text-sm">
              <span className="text-gray-600">Already have an account? </span>
              <Link 
                to="/login" 
                className="font-medium hover:underline"
                style={{ color: '#009245' }}
                data-testid="login-link"
              >
                Sign In
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignupPage;
